import { RequireSomeFields } from 'common/TypeUtilities';
import React from 'react';
import { useChatContext } from 'src/chat/hooks/useChatContext';
import { DietaryDetails, OrderShefFragment } from 'src/gqlReactTypings.generated.d';
import { KitchenPractices } from 'src/pages/consumer/order/components/ShefDescriptionCards/verified-dietary/KitchenPractices';
import { ButtonColors } from 'src/shared/design-system/Buttons';
import { ChipColor } from 'src/shared/design-system/Chip';
import { WavyEdge } from 'src/shared/design-system/DishFirst/WavyEdge';
import { BowlWithWhisk } from 'src/shared/design-system/Icon';
import { ModalProps } from 'src/shared/design-system/Modal';
import { TrackedNavLink } from 'src/shared/design-system/TrackedNavLink';
import { VerticallyCenteredText } from 'src/shared/design-system/VerticallyCenteredText';
import { Routes } from 'src/shared/Routes';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import Chat from 'web-common/src/shared/design-system/icons/Chat.svg';
import { Colors } from 'web-common/src/shared/styles';
import {
  BackButton,
  BackButtonContainer,
  DietaryDisclosuresModal,
  FooterButton,
  Header,
  QuestionsButton,
  Section,
  SectionTextDarker,
  SectionTitle,
  StyledChip,
  StyledDivider,
  StyledModalBody,
  StyledModalFooter,
  Title,
} from './styles';

interface IProps {
  shef: OrderShefFragment;
  dietaryDetails: DietaryDetails;
}

export const DietaryDisclosureModal: React.FC<IProps & RequireSomeFields<ModalProps, 'onClose'>> = ({
  shef,
  dietaryDetails,
  onClose,
  isShowing,
}) => {
  const { isSmallScreen } = useScreenContext();
  const { openWidget } = useChatContext();

  return (
    <DietaryDisclosuresModal
      onClose={onClose}
      isShowing={isShowing}
      size='lg'
      isRounded={false}
      isFullWidth={isSmallScreen}
      isFullHeight={isSmallScreen}>
      <Header>
        <BackButtonContainer className='mr-2 text-xs' onClick={onClose}>
          <BackButton>
            <i className='fal fa-chevron-left' />
          </BackButton>
        </BackButtonContainer>
        <VerticallyCenteredText>{dietaryDetails.name}</VerticallyCenteredText>
      </Header>
      <StyledModalBody>
        <Section background={Colors.KOHLRABI_LITE_30}>
          <div className='mb-2 flex flex-row items-center justify-start gap-4'>
            <BowlWithWhisk width={32} height={32} />
            <Title>Shef {shef.firstName}'s Kitchen</Title>
          </div>
          <WavyEdge amplitude={16} frequency={24} />
        </Section>

        <Section>
          <StyledChip
            className='mb-4'
            icon={<i className='fas fa-badge-check mr-2' />}
            text={dietaryDetails.summaryDisplay}
            color={ChipColor.KOHLRABI}
            showShadow={false}
            allAboveBaseline
          />
          <SectionTextDarker className='mb-4'>{dietaryDetails.summaryExplanation}</SectionTextDarker>
          <SectionTitle className='mb-4'>{shef.firstName}'s kitchen practices</SectionTitle>
          <KitchenPractices className='mb-4' dietaryDetails={dietaryDetails} />
        </Section>

        <StyledDivider />
        <Section>
          <SectionTitle className='mb-2'>Have more questions?</SectionTitle>
          <SectionTextDarker className='mb-2'>
            Need more transparency about the dishes being prepared?
          </SectionTextDarker>
          <div className='flex flex-row gap-2 '>
            <TrackedNavLink
              className='flex grow'
              to={{ pathname: Routes.HELP_FAQ_PATH }}
              target='_blank'
              name='help-center'>
              <QuestionsButton color={ButtonColors.WHITE}>
                <i className='fal fa-info-circle mr-4' />
                <VerticallyCenteredText>Contact support</VerticallyCenteredText>
              </QuestionsButton>
            </TrackedNavLink>
            {openWidget && !shef.celebrityEventType && shef.shefProfile.allowedToChat && (
              <QuestionsButton
                color={ButtonColors.WHITE}
                onClick={() => {
                  onClose();
                  openWidget(shef);
                }}>
                <div className='relative justify-start rounded-full border-2 border-eggplant-50 bg-rice'>
                  <img src={Chat} alt='Chat' className='flex size-4 justify-end' />
                </div>
                <VerticallyCenteredText>Message shef</VerticallyCenteredText>
              </QuestionsButton>
            )}
          </div>
        </Section>
      </StyledModalBody>
      <StyledModalFooter>
        <FooterButton onClick={onClose}>Back to {shef.firstName}'s menu</FooterButton>
      </StyledModalFooter>
    </DietaryDisclosuresModal>
  );
};
